/*
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2025 Pearson Education, Inc.
 * All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained
 * from Pearson Education, Inc.
 */

import Framework, { BaseService } from '@greenville/framework';
import * as constants from '../../constants/ServiceConstants';
import utils from '../../../../common/utils';

/**
 * GetUploadedContentService - Fetches uploaded content for Content Drawer
 * API: GET /api/v2.0/upload/content?userId=xxx
 */
export default class GetUploadedContentService extends BaseService {
  constructor() {
    super(constants.GET_UPLOADED_CONTENT, 'getUploadedContentStatus');
  }

  async handleEvent(eventName, event) {
    const { payload, headers } = utils.buildRequestPartsFromPayload(event);
    const { userId } = payload;

    const { isStackB } = utils.getStackBParams();

    if (isStackB) {
      const enhancedHeaders = await utils.resolveStackBHeaders(headers);
      return this.getClient('aiadminstackbv2').get(`/upload/content?userId=${encodeURIComponent(userId)}`, {
        headers: enhancedHeaders
      });
    }
    return this.getClient('aiadminv2').get(`/upload/content?userId=${encodeURIComponent(userId)}`, { headers });
  }

  handleResponse(response) {
    const { data } = response;
    if (data && data.success) {
      Framework.getStoreRegistry().getStore('contentUploadModel').setContents(data.data);
      Framework.getEventManager().publish(constants.GET_UPLOADED_CONTENT_SUCCESS, { data: data.data });
    } else {
      const serverMessage = data?.error || data?.message;
      Framework.getStoreRegistry()
        .getStore('contentUploadModel')
        .setLoadError(serverMessage || 'contentUpload.FETCH_FAILED');
      Framework.getEventManager().publish(constants.GET_UPLOADED_CONTENT_ERROR);
    }
  }

  handleError(error) {
    const { response } = error;

    if (response) {
      const { status, data } = response;
      if (status === 401) {
        utils.redirectToLogin();
        Framework.getEventManager().publish(constants.GET_UPLOADED_CONTENT_ERROR);
        return;
      }
      const serverMessage = data?.error || data?.message;
      Framework.getStoreRegistry()
        .getStore('contentUploadModel')
        .setLoadError(serverMessage || 'contentUpload.FETCH_REQUEST_FAILED');
      Framework.getEventManager().publish(constants.GET_UPLOADED_CONTENT_ERROR);
      return;
    }

    const langMessage =
      error.message === `timeout of ${constants.BERLIN_API_TIMEOUT}ms exceeded`
        ? 'contentUpload.REQUEST_TIMEOUT'
        : 'contentUpload.FETCH_REQUEST_FAILED';

    Framework.getStoreRegistry().getStore('contentUploadModel').setLoadError(langMessage);
    Framework.getEventManager().publish(constants.GET_UPLOADED_CONTENT_ERROR);
  }
}
